import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, API, Footer, FRONTEND_URL } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Car, ArrowRight, Shield, Clock, Zap, Check, X, ChevronRight, FileText, DollarSign, TrendingUp, Fingerprint, Store, Compass } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useWidgetMode } from "@/lib/widgetMode";
import { US_STATES } from "@/lib/usStates";
import { brandConfig } from "@/lib/brandConfig";

// Shared by the full hero card and the compact (form-only) render below.
const VinForm = ({
  activeTab, setActiveTab,
  vin, setVin,
  plate, setPlate,
  plateState, setPlateState,
  error, setError,
  loading, onSubmit,
}) => {
  const canSubmit = activeTab === 'vin' ? vin.length === 17 : !!plate.trim() && !!plateState;

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-6">
        <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setError(''); }}>
          <TabsList className="grid grid-cols-2 w-full h-11 mb-5" data-testid="entry-mode-tabs">
            <TabsTrigger value="plate" data-testid="plate-tab">License Plate</TabsTrigger>
            <TabsTrigger value="vin" data-testid="vin-tab">VIN</TabsTrigger>
          </TabsList>

          <TabsContent value="plate" className="mt-0 space-y-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-slate-700">
                License Plate
              </label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    data-testid="plate-input"
                    placeholder="License Plate"
                    value={plate}
                    onChange={(e) => {
                      setPlate(e.target.value.toUpperCase());
                      setError('');
                    }}
                    className="pl-12 h-14 text-lg tracking-wider uppercase rounded-xl border-slate-300 focus:border-slate-500 focus:ring-slate-500/20"
                    maxLength={10}
                  />
                </div>
                <Select
                  value={plateState}
                  onValueChange={(value) => { setPlateState(value); setError(''); }}
                >
                  <SelectTrigger data-testid="plate-state-select" className="w-28 h-14 rounded-xl border-slate-300 text-lg">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                We'll look up your vehicle from your plate and state
              </p>
            </div>
          </TabsContent>

          <TabsContent value="vin" className="mt-0 space-y-4">
            <div>
              <label className="block mb-2 text-sm font-medium text-slate-700">
                Vehicle Identification Number (VIN)
              </label>
              <div className="relative">
                <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  data-testid="vin-input"
                  placeholder="Enter 17-character VIN"
                  value={vin}
                  onChange={(e) => {
                    setVin(e.target.value.toUpperCase());
                    setError('');
                  }}
                  className="pl-12 h-14 text-lg tracking-wider uppercase rounded-xl border-slate-300 focus:border-slate-500 focus:ring-slate-500/20"
                  maxLength={17}
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Find your VIN on the driver's side dashboard or door jamb
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {error && (
          <p className="text-sm text-red-600" data-testid="entry-error">{error}</p>
        )}

        <Button
          type="submit"
          data-testid="get-offer-btn"
          disabled={loading || !canSubmit}
          className="w-full h-14 text-lg btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-slate-800/30 border-t-slate-800 rounded-full animate-spin" />
              {activeTab === 'vin' ? 'Decoding VIN...' : 'Looking Up Plate...'}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Explore My Options
              <ArrowRight className="w-5 h-5" />
            </span>
          )}
        </Button>
      </form>

      <p className="text-center text-xs text-slate-500 mt-4">
        No pressure. No obligation. No phone or email required to get started.
      </p>
    </>
  );
};

// Bar mode's ~100px-tall render (see widgetMode.isBar). Deliberately a
// separate component from VinForm rather than a shared/branching one — the
// layout is a single horizontal row (tab toggle + input(s) + button), not a
// condensed version of the stacked labeled-field layout the card/compact
// forms use, so there's little to actually share beyond the state itself.
const VinBar = ({
  activeTab, setActiveTab,
  vin, setVin,
  plate, setPlate,
  plateState, setPlateState,
  loading, onSubmit,
}) => {
  const canSubmit = activeTab === 'vin' ? vin.length === 17 : !!plate.trim() && !!plateState;

  return (
    <form onSubmit={onSubmit} className="flex items-center gap-2 @sm:gap-3 flex-wrap @sm:flex-nowrap">
      <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 shrink-0" data-testid="bar-entry-mode-tabs">
        <button
          type="button"
          data-testid="bar-plate-tab"
          onClick={() => setActiveTab('plate')}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'plate' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
        >
          Plate
        </button>
        <button
          type="button"
          data-testid="bar-vin-tab"
          onClick={() => setActiveTab('vin')}
          className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'vin' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
        >
          VIN
        </button>
      </div>

      {activeTab === 'plate' ? (
        // min-w-[150px] (not min-w-0) is load-bearing: the state select inside
        // is shrink-0, so if this wrapper were allowed to collapse toward 0
        // when the row runs out of space, the select would overflow its own
        // collapsed box instead of the outer flex-wrap row actually wrapping —
        // visually landing underneath/behind whatever flex item comes next
        // (the submit button). Giving it a real minimum makes the outer row
        // wrap onto a new line instead, which is what should happen.
        <div className="flex items-center gap-2 flex-1 min-w-[150px]">
          <Input
            type="text"
            data-testid="bar-plate-input"
            placeholder="Plate #"
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            className="h-9 text-sm uppercase tracking-wide rounded-lg border-slate-300 min-w-0 flex-[2]"
            maxLength={10}
          />
          <Select value={plateState} onValueChange={setPlateState}>
            <SelectTrigger data-testid="bar-plate-state-select" className="h-9 rounded-lg border-slate-300 text-sm flex-1 min-w-[88px]">
              <SelectValue placeholder="ST" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Input
          type="text"
          data-testid="bar-vin-input"
          placeholder="Enter VIN"
          value={vin}
          onChange={(e) => setVin(e.target.value.toUpperCase())}
          className="h-9 text-sm uppercase tracking-wide rounded-lg border-slate-300 flex-1 min-w-0"
          maxLength={17}
        />
      )}

      <Button
        type="submit"
        data-testid="bar-get-offer-btn"
        disabled={loading || !canSubmit}
        // w-full below @sm deliberately forces the button onto its own line
        // whenever the row wraps, rather than leaving it to incidentally
        // land there based on exact tab/input/select widths — the tabs +
        // plate/VIN entry then have the full row to themselves above it.
        className="h-9 px-4 text-sm btn-accent disabled:opacity-50 disabled:cursor-not-allowed shrink-0 whitespace-nowrap w-full @sm:w-auto"
      >
        {loading ? 'Looking Up...' : 'Explore My Options'}
      </Button>
    </form>
  );
};

const VINEntryScreen = ({ state, updateState, resetState, onVehicleDecoded }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plate');
  const [vin, setVin] = useState(state.vin || '');
  const [plate, setPlate] = useState('');
  const [plateState, setPlateState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { isEmbedded: isWidgetMode, isCompact, isBar } = useWidgetMode();

  useEffect(() => {
    // Reset state when landing on home
    resetState();
  }, [resetState]);

  const validateVIN = (vin) => {
    const cleanVin = vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    return cleanVin.length === 17;
  };

  const goToVehicleConfirm = (vehicle) => {
    updateState({ vin: vehicle.vin, vehicleInfo: vehicle });
    // Bar mode never routes internally to /confirm-vehicle — a ~100px strip
    // has nowhere to render it. It hands the decoded vehicle off via
    // onVehicleDecoded, which by default opens a separate widget instance in
    // the built-in modal dialog already on that screen (index.js's
    // resolveVehicleDecodedHandler / lib/widgetModal.js).
    if (isBar) {
      onVehicleDecoded?.(vehicle);
    } else {
      navigate('/confirm-vehicle');
    }
  };

  const handleVinSubmit = async () => {
    const cleanVin = vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');

    if (!validateVIN(cleanVin)) {
      const message = 'Please enter a valid 17-character VIN';
      setError(message);
      // Bar mode has no room for inline error text, so surface it as a
      // toast instead — same reasoning as the API-failure catch blocks
      // below, which already toast for every mode.
      if (isBar) toast.error(message);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/offers/decode`, { vin: cleanVin });
      if (response.data.success) {
        goToVehicleConfirm(response.data.vehicle);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to decode VIN. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlateSubmit = async () => {
    const cleanPlate = plate.trim().toUpperCase();

    if (!cleanPlate || !plateState) {
      const message = 'Please enter your license plate and select a state';
      setError(message);
      if (isBar) toast.error(message);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/offers/decode-plate`, {
        plate: cleanPlate,
        state: plateState,
      });
      if (response.data.success) {
        goToVehicleConfirm(response.data.vehicle);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to look up that plate. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (activeTab === 'vin') {
      handleVinSubmit();
    } else {
      handlePlateSubmit();
    }
  };

  // Bar mode (initNucarNext's `bar` option) — a short strip meant to sit
  // inline under eg. a VDP's main photo, not as a full landing surface:
  // logo + a condensed version of the hero's tagline on top, the plate/VIN
  // lookup + CTA on its own full-width row below (was squeezed onto the
  // same row as the logo originally — too little room for the form at
  // typical VDP-photo widths, which caused the CTA button to visually
  // collide with the state dropdown). On a successful decode it doesn't
  // navigate itself (see goToVehicleConfirm above) — the host page is
  // expected to open a separate widget instance (the modal pattern)
  // already on the vehicle-confirm screen via onVehicleDecoded.
  if (isBar) {
    return (
      <div
        className="px-4 @sm:px-5 py-3 @sm:py-4 header-nucar border border-white/10 rounded-xl"
        data-testid="vin-entry-bar"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="shrink-0 flex items-center">
            {brandConfig.noLogo ? (
              <span className="font-bold text-base text-white" data-testid="nucarnext-logo">{brandConfig.name}</span>
            ) : (
              <img src={brandConfig.logoLight} alt={brandConfig.name} className="h-7" data-testid="nucarnext-logo" />
            )}
          </div>
          <p className="text-sm @sm:text-base font-semibold text-white leading-snug">
            Sell your car online in minutes, for the{' '}
            <span className="text-accent-yellow">best price.</span>
          </p>
        </div>
        <div className="bg-white rounded-lg p-2 @sm:p-2.5">
          <VinBar
            activeTab={activeTab} setActiveTab={setActiveTab}
            vin={vin} setVin={setVin}
            plate={plate} setPlate={setPlate}
            plateState={plateState} setPlateState={setPlateState}
            loading={loading} onSubmit={handleSubmit}
          />
        </div>
      </div>
    );
  }

  // Compact mode (initNucarNext's `compact` option) — no marketing copy,
  // just the form. Meant for small-footprint embeds like the inline-under-
  // a-button pattern, which pushes a whole page's content down as it
  // expands, unlike a modal or the full standalone page.
  if (isCompact) {
    return (
      <div className="bg-white rounded-2xl p-6 @sm:p-8 border border-slate-200 shadow-sm">
        <VinForm
          activeTab={activeTab} setActiveTab={setActiveTab}
          vin={vin} setVin={setVin}
          plate={plate} setPlate={setPlate}
          plateState={plateState} setPlateState={setPlateState}
          error={error} setError={setError}
          loading={loading} onSubmit={handleSubmit}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section - Dark Navy */}
      <div className="header-nucar">
        <Header variant="dark" />
        
        <section className="py-16 @sm:py-24">
          <div className="container-wide">
            <div className="grid @lg:grid-cols-2 gap-12 items-center">
              {/* Left - Copy */}
              <div className="opacity-0 animate-fade-in-up">
                <h1 className="text-4xl @sm:text-5xl @lg:text-6xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sell your car online in minutes, for the{' '}
                  <span className="text-accent-yellow">best price.</span>
                </h1>
                <p className="text-lg text-slate-300 mb-8 max-w-xl">
                  Say goodbye to outdated trade-widgets, say hello to what's Next. 
                  NucarNext is a guided experience that helps you value, compare, plan, and decide what's next for your car.
                </p>
                
                {/* Trust badges */}
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Shield className="w-5 h-5 text-accent-yellow" />
                    <span className="text-sm">No obligation</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Zap className="w-5 h-5 text-accent-yellow" />
                    <span className="text-sm">Instant results</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Clock className="w-5 h-5 text-accent-yellow" />
                    <span className="text-sm">Free forever</span>
                  </div>
                </div>
              </div>

              {/* Right - VIN Entry Card */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl opacity-0 animate-fade-in-up animate-stagger-2">
                <VinForm
                  activeTab={activeTab} setActiveTab={setActiveTab}
                  vin={vin} setVin={setVin}
                  plate={plate} setPlate={setPlate}
                  plateState={plateState} setPlateState={setPlateState}
                  error={error} setError={setError}
                  loading={loading} onSubmit={handleSubmit}
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Why NucarNext — 6 Tile Grid */}
      <section className="py-20 bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-1 @md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {/* Tile 1 — Vehicle Passport (copy) */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col justify-between" data-testid="tile-vehicle-passport">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'linear-gradient(135deg, hsl(220, 70%, 94%) 0%, hsl(48, 90%, 92%) 100%)' }}>
                  <Fingerprint className="w-6 h-6" style={{ color: 'hsl(220, 70%, 30%)' }} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Vehicle Passport</h3>
                <p className="text-sm font-medium mb-4" style={{ color: 'hsl(48, 80%, 40%)' }}>Powered by AI</p>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">See everything about your car — instantly</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  No guessing. No endless drop-downs. We decode your exact vehicle in seconds — trim, packages, options, and details down to the smallest features — so you know exactly what you have and what it's worth.
                </p>
              </div>
            </div>

            {/* Tile 2 — Chevy Image */}
            <div className="rounded-2xl overflow-hidden aspect-[4/3] @md:aspect-auto" data-testid="tile-image-chevy">
              <img src={`${FRONTEND_URL}/nucar-chevy.jpg`} alt="Nucar Chevrolet" className="w-full h-full object-cover" loading="lazy" />
            </div>

            {/* Tile 3 — One Powerful Offer (copy) */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col justify-between" data-testid="tile-powerful-offer">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'linear-gradient(135deg, hsl(48, 90%, 92%) 0%, hsl(220, 70%, 94%) 100%)' }}>
                  <Store className="w-6 h-6" style={{ color: 'hsl(48, 80%, 35%)' }} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">One Powerful Offer</h3>
                <p className="text-sm font-medium mb-4" style={{ color: 'hsl(48, 80%, 40%)' }}>Backed by 32 stores across 5 states</p>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">Real buying power behind every number</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  This isn't just an estimate — it's a real offer. Powered by demand across our entire network, your offer is built to be competitive, confident, and ready when you are — whether you sell, trade, or simply explore.
                </p>
              </div>
            </div>

            {/* Tile 4 — Honda Image */}
            <div className="rounded-2xl overflow-hidden aspect-[4/3] @md:aspect-auto" data-testid="tile-image-honda">
              <img src={`${FRONTEND_URL}/nucar-honda.jpg`} alt="Nucar Honda" className="w-full h-full object-cover" loading="lazy" />
            </div>

            {/* Tile 5 — Decide What's Next (copy) */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col justify-between" data-testid="tile-decide-next">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'linear-gradient(135deg, hsl(220, 70%, 94%) 0%, hsl(48, 90%, 92%) 100%)' }}>
                  <Compass className="w-6 h-6" style={{ color: 'hsl(220, 70%, 30%)' }} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Decide What's Next</h3>
                <p className="text-sm font-medium mb-4" style={{ color: 'hsl(48, 80%, 40%)' }}>Clarity, confidence, and zero pressure</p>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">Explore everything about your car</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Get the full picture in seconds — market insights, vehicle history, recall checks, and more. No obligation. No personal info required. Just everything you need to make the right move, at your pace.
                </p>
              </div>
            </div>

            {/* Tile 6 — Toyota Image */}
            <div className="rounded-2xl overflow-hidden aspect-[4/3] @md:aspect-auto" data-testid="tile-image-toyota">
              <img src={`${FRONTEND_URL}/nucar-toyota.jpg`} alt="Nucar Toyota" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Why NucarNext — Comparison Cards */}
      <section className="py-20 bg-slate-50">
        <div className="container-wide">
          <div className="text-center mb-14">
            <span className="inline-block text-sm font-medium text-slate-600 px-4 py-1.5 rounded-full mb-4" style={{ backgroundColor: 'hsl(48, 90%, 92%)' }}>
              Why NucarNext?
            </span>
            <h2 className="text-3xl @sm:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Not your grandfather's trade-in tool
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">Most online trade tools are just lead-gen forms. We built something actually useful.</p>
          </div>

          <div className="grid @md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {[
              {
                title: "Exact Vehicle Decode",
                desc: "We decode your exact trim, packages, and options — not just make, model, year.",
                us: "NucarNext",
                them: "Generic trim assumptions",
                icon: <Car className="w-6 h-6 text-white" />,
              },
              {
                title: "Recall & Ownership History",
                desc: "Open recalls, owner count, and service events all visible instantly.",
                us: "NucarNext",
                them: "Hidden or extra cost",
                icon: <Shield className="w-6 h-6 text-white" />,
              },
              {
                title: "Transparent Dealer Math",
                desc: "See exactly how we calculate dealer purchase price vs. retail listings.",
                us: "NucarNext",
                them: "Black box pricing",
                icon: <FileText className="w-6 h-6 text-white" />,
              },
              {
                title: "Multiple Real Paths",
                desc: "Sell, trade, keep, or track — all legitimate options, not just one outcome.",
                us: "NucarNext",
                them: "Lead capture disguised as value",
                icon: <Compass className="w-6 h-6 text-white" />,
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-7 border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    <div className="flex items-center gap-6 mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-emerald-700">{item.us}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <X className="w-4 h-4 text-slate-300" />
                        <span className="text-sm text-slate-400">{item.them}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section — disabled until we have real customer reviews to show
      <section className="py-20 bg-white">
        <div className="container-wide">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2">Customer Stories</p>
            <h2 className="text-3xl @sm:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Clarity creates confidence
            </h2>
            <p className="text-slate-600 mt-3">See what others are saying about their NucarNext experience.</p>
          </div>

          <div className="grid @md:grid-cols-3 gap-8">
            {[
              { quote: "I was dreading the trade-in process, but NucarNext made it so easy. I knew exactly what my car was worth before I even talked to anyone.", name: "Sarah M.", location: "Wilmington, DE", car: "2020 Honda CR-V" },
              { quote: "The transparency is what got me. Seeing the actual dealer math behind the offer built so much trust. No other site does this.", name: "Michael T.", location: "Newark, DE", car: "2019 Toyota Camry" },
              { quote: "I decided to keep my car after seeing the full picture. NucarNext helped me understand what maintenance was coming up.", name: "Jennifer K.", location: "New Castle, DE", car: "2021 Ford Escape" }
            ].map((t) => (
              <div key={t.name} className="testimonial-card">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-slate-700 mb-6">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-sm text-slate-500">{t.location}</p>
                  <p className="text-sm text-slate-400">{t.car}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* CTA Section */}
      {!isWidgetMode && (
        <>
          <section className="py-20 header-nucar">
            <div className="container-wide text-center">
              <h2 className="text-3xl @sm:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ready to see what your car is really worth?
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto">
                Get your free Vehicle Passport and instant offers in under 2 minutes. No contact info required.
              </p>
              <div className="flex flex-col @sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="btn-accent h-14 px-8"
                >
                  Get Started Free
                </Button>
                <Button 
                  onClick={() => navigate('/how-it-works')}
                  variant="outline"
                  className="h-14 px-8 bg-transparent border-slate-600 text-white hover:bg-slate-800"
                >
                  Learn How It Works
                </Button>
              </div>
              <p className="text-slate-500 text-sm mt-6">100% free. No spam. No obligation.</p>
            </div>
          </section>

          <Footer />
        </>
      )}
    </div>
  );
};

export default VINEntryScreen;
